import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import type { Lead, LeadFunnelMembership, SalesFunnel, Stage } from "@/types/lead";
import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";

export type LeadFunnelPlacement = {
  funnelId: string;
  funnelName: string;
  funnelColor: string;
  stageSlug: string;
  stageName: string;
  stageColor: string;
};

type FunnelRow = Pick<SalesFunnel, "id" | "name" | "color" | "integration_id" | "is_default">;
type MembershipRow = Pick<LeadFunnelMembership, "funnel_id" | "stage" | "order_index">;
type LeadRow = Pick<Lead, "funnel_id" | "stage">;

export type PickPrimaryPlacementInput = {
  lead: LeadRow;
  memberships: MembershipRow[];
  funnels: FunnelRow[];
  preferIntegrationId?: string | null;
};

export type PrimaryPlacement = {
  funnelId: string;
  stageSlug: string;
};

/**
 * Resolve o slug de estágio para um valor existente em lead_stages (case-insensitive).
 */
export function normalizeStageSlug(
  rawSlug: string | null | undefined,
  stagesForFunnel: Pick<Stage, "slug">[],
): string {
  const fallback = stagesForFunnel[0]?.slug || rawSlug || "novo";
  if (!rawSlug) return fallback;

  const exact = stagesForFunnel.find((s) => s.slug === rawSlug);
  if (exact) return exact.slug;

  const lower = rawSlug.toLowerCase();
  const caseInsensitive = stagesForFunnel.find((s) => s.slug.toLowerCase() === lower);
  if (caseInsensitive) return caseInsensitive.slug;

  return fallback;
}

/**
 * Escolhe o funil/estágio canônico do lead — mesma regra do kanban CRM:
 * 1) leads.funnel_id + stage (default / move_lead_to_stage)
 * 2) membership do funil da conversa (integration_id), se lead sem funnel_id
 * 3) membership do funil padrão do workspace
 * 4) primeira membership em funil não-integração
 * 5) primeira membership por order_index
 */
export function pickPrimaryLeadPlacement({
  lead,
  memberships,
  funnels,
  preferIntegrationId,
}: PickPrimaryPlacementInput): PrimaryPlacement | null {
  const funnelById = new Map(funnels.map((f) => [f.id, f]));

  if (lead.funnel_id) {
    const membership = memberships.find((m) => m.funnel_id === lead.funnel_id);
    return {
      funnelId: lead.funnel_id,
      stageSlug: membership?.stage || lead.stage || "novo",
    };
  }

  if (preferIntegrationId) {
    const integrationFunnel = funnels.find((f) => f.integration_id === preferIntegrationId);
    if (integrationFunnel) {
      const membership = memberships.find((m) => m.funnel_id === integrationFunnel.id);
      if (membership) {
        return { funnelId: membership.funnel_id, stageSlug: membership.stage || "novo" };
      }
    }
  }

  const defaultFunnel = funnels.find((f) => f.is_default);
  if (defaultFunnel) {
    const membership = memberships.find((m) => m.funnel_id === defaultFunnel.id);
    if (membership) {
      return { funnelId: membership.funnel_id, stageSlug: membership.stage || "novo" };
    }
  }

  const nonIntegrationMembership = memberships.find((m) => {
    const funnel = funnelById.get(m.funnel_id);
    return funnel && !funnel.integration_id;
  });
  if (nonIntegrationMembership) {
    return {
      funnelId: nonIntegrationMembership.funnel_id,
      stageSlug: nonIntegrationMembership.stage || "novo",
    };
  }

  if (memberships.length > 0) {
    const sorted = [...memberships].sort((a, b) => a.order_index - b.order_index);
    return {
      funnelId: sorted[0].funnel_id,
      stageSlug: sorted[0].stage || "novo",
    };
  }

  if (lead.stage) {
    return { funnelId: "", stageSlug: lead.stage };
  }

  return null;
}

export function sortMembershipsPrimaryFirst(
  memberships: LeadFunnelMembership[],
  primaryFunnelId: string | null | undefined,
): LeadFunnelMembership[] {
  if (!primaryFunnelId) {
    return [...memberships].sort((a, b) => a.order_index - b.order_index);
  }
  return [...memberships].sort((a, b) => {
    const aPrimary = a.funnel_id === primaryFunnelId ? 0 : 1;
    const bPrimary = b.funnel_id === primaryFunnelId ? 0 : 1;
    if (aPrimary !== bPrimary) return aPrimary - bPrimary;
    return a.order_index - b.order_index;
  });
}

export async function fetchLeadFunnelPlacement(
  supabase: SupabaseClient<Database>,
  leadId: string,
  integrationById: WorkspaceIntegrationsMap,
  options?: { preferIntegrationId?: string | null; workspaceId?: string | null },
): Promise<LeadFunnelPlacement | null> {
  const [{ data: leadRow }, { data: memberships }] = await Promise.all([
    supabase.from("leads").select("funnel_id, stage, workspace_id").eq("id", leadId).maybeSingle(),
    supabase
      .from("lead_funnel_memberships")
      .select("funnel_id, stage, order_index")
      .eq("lead_id", leadId)
      .order("order_index", { ascending: true }),
  ]);

  if (!leadRow) return null;

  const workspaceId = options?.workspaceId ?? leadRow.workspace_id;
  let funnels: FunnelRow[] = [];

  if (workspaceId) {
    const { data: funnelRows } = await supabase
      .from("sales_funnels")
      .select("id, name, integration_id, color, is_default")
      .eq("workspace_id", workspaceId)
      .order("order_index", { ascending: true });
    funnels = funnelRows || [];
  }

  const primary = pickPrimaryLeadPlacement({
    lead: leadRow,
    memberships: memberships || [],
    funnels,
    preferIntegrationId: options?.preferIntegrationId,
  });

  if (!primary?.funnelId) return null;

  const slug = primary.stageSlug || "novo";
  const [{ data: funnelRow }, { data: stageRows }] = await Promise.all([
    supabase
      .from("sales_funnels")
      .select("id, name, integration_id, color")
      .eq("id", primary.funnelId)
      .maybeSingle(),
    supabase
      .from("lead_stages")
      .select("name, color, slug, order_index")
      .eq("funnel_id", primary.funnelId)
      .order("order_index", { ascending: true }),
  ]);

  if (!funnelRow) return null;

  const normalizedSlug = normalizeStageSlug(slug, stageRows || []);
  const stageRow =
    stageRows?.find((s) => s.slug === normalizedSlug) ||
    stageRows?.find((s) => s.slug.toLowerCase() === normalizedSlug.toLowerCase());

  return {
    funnelId: primary.funnelId,
    funnelName: resolveFunnelDisplayName(funnelRow, integrationById),
    funnelColor: funnelRow.color || "bg-blue-500",
    stageSlug: normalizedSlug,
    stageName: stageRow?.name || normalizedSlug,
    stageColor: stageRow?.color || "bg-gray-500",
  };
}

export async function persistLeadFunnelPlacement(
  supabase: SupabaseClient<Database>,
  leadId: string,
  funnelId: string,
  stageSlug: string,
): Promise<void> {
  const { data: existing } = await supabase
    .from("lead_funnel_memberships")
    .select("id")
    .eq("lead_id", leadId)
    .eq("funnel_id", funnelId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from("lead_funnel_memberships")
      .update({ stage: stageSlug })
      .eq("lead_id", leadId)
      .eq("funnel_id", funnelId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("lead_funnel_memberships").insert({
      lead_id: leadId,
      funnel_id: funnelId,
      stage: stageSlug,
      order_index: 0,
    });
    if (error) throw error;
  }

  const { error: leadError } = await supabase
    .from("leads")
    .update({ funnel_id: funnelId, stage: stageSlug })
    .eq("id", leadId);
  if (leadError) throw leadError;
}
